'use strict';

import "core-js/stable"
import "regenerator-runtime/runtime" // Required by d3.

import 'd3-transition'
import React from 'react'
import { select, selectAll} from 'd3-selection'
import { scaleLinear } from 'd3-scale'
import { max, min, range } from 'd3-array'
import { axisBottom, axisLeft } from 'd3-axis'
import { formatTime, metersPerSecondAsSecondsPerHundredMeters } from '../../common/helpers.js'
import { formatSecondsAsPace, } from './helpers.js'

const chart_margin = {top: 15, right: 15, bottom: 17, left: 55};

function formatMinutesFromSeconds(seconds) {
  return formatTime(seconds, false, false, false);
}

export class ChartSectionSwimLapsPace extends React.Component {

  chart_height_with_margin = 200
  chart_height = this.chart_height_with_margin - chart_margin.top - chart_margin.bottom;

  createChart = () => {

    this.updateLapData();

    let container_id = '#activity-' + this.props.activity_id + ' .swim-speed-chart-container';
    let width = $(container_id).width() - chart_margin.left - chart_margin.right;

    this.chart_container = select(container_id + ' .chart')
      .attr("width", width + chart_margin.left + chart_margin.right);

    this.chart = this.chart_container.append("g")
      .attr("transform", "translate(" + chart_margin.left + "," + chart_margin.top + ")");

    // Set the scales
    this.x_scale = scaleLinear()
      .range([0, width])

    this.y_scale = scaleLinear()
      .range([this.chart_height, 0]);

    // Y axis label.
    this.y_axis_label = this.chart.append("text")
      .attr("class", "y-axis-label")
      .style("font-size", "12px")
      .style("text-anchor", "middle")
      .text('Pace min/100m')
      .attr("transform", "rotate(270)")
      .attr("dx", 0 - (this.chart_height / 2))
      .attr("dy", 15 - chart_margin.left);

    let min_pace = min(this.lap_data, d => d.seconds_per_hectometer);
    min_pace = min_pace - 30; // Put some space at the bottom of the graph.
    min_pace = min_pace < 0 ? 0 : min_pace;
    this.y_scale.domain([
      min_pace,
      max(this.lap_data, d => d.seconds_per_hectometer)
    ]);

    this.x_axis = axisBottom()
      .scale(this.x_scale)

    let y_axis = axisLeft()
      .scale(this.y_scale)
      .ticks(4)
      .tickFormat(formatMinutesFromSeconds);

    // Add the x-axis
    this.x_axis_group = this.chart.append("g")
      .attr("transform", `translate(0, ${this.chart_height})`)
      .attr("class", "x axis lap-duration")
      .call(this.x_axis);

    // Add the y-axis
    this.chart.append("g")
      .attr("class", "y axis swim-duration")
      .call(y_axis);

    this.length_bar_group = this.chart.append("g")
      .attr("class", "length-bars")

    this.lap_bar_group = this.chart.append("g")
      .attr("class", "lap-bars")

    this.bar_separator_group = this.chart.append("g")
      .attr("class", "bar-separators")

    this.addPaceLineToChart(this.chart_container, this.chart, this.y_axis_label, this.chart_height);

  }

  updateLapData = () => {
    this.lap_data = [];
    this.length_data = [];

    this.cumulative_seconds = 0;
    this.cumulative_meters = 0;
    Object.keys(this.props.laps).forEach((lap_id) => {

      const original_lap = this.props.laps[lap_id];

      if (original_lap.creator != 'device' || original_lap.visible == false) {
        return;
      }

      let seconds_per_hectometer = null;
      let duration_seconds = 0;
      let distance_meters = 0;
      if (original_lap.swim_stroke != 'resting') {
        seconds_per_hectometer = metersPerSecondAsSecondsPerHundredMeters(original_lap.get_speed_meters_per_second());
        distance_meters = original_lap.distance_meters;
      }

      let lap = {
        id: original_lap.id,
        swim_stroke: original_lap.swim_stroke,
        distance_meters: distance_meters,
        duration_seconds: original_lap.duration_seconds,
        cumulative_seconds: this.cumulative_seconds,
        cumulative_meters: this.cumulative_meters,
        seconds_per_hectometer: seconds_per_hectometer,
        lengths_visible: original_lap.lengths_visible,
      };

      if (original_lap.lengths_visible) {

        let cumulative_lengths_meters = this.cumulative_meters;
        let cumulative_lengths_seconds = this.cumulative_seconds;

        Object.keys(original_lap.lengths).forEach((index) => {

          const length = original_lap.lengths[index];

          let length_data = {
            id: length.id,
            swim_stroke: length.stroke,
            stroke_count: length.strokes,
            distance_meters: length.distance_meters,
            duration_seconds: length.duration_seconds,
            cumulative_meters: cumulative_lengths_meters,
            cumulative_seconds: cumulative_lengths_seconds,
            seconds_per_hectometer: metersPerSecondAsSecondsPerHundredMeters(length.get_speed_meters_per_second()),
          };

          cumulative_lengths_meters += length.distance_meters;
          cumulative_lengths_seconds += length.duration_seconds;

          this.length_data.push(length_data);

        });

      }

      this.cumulative_seconds += lap.duration_seconds;
      this.cumulative_meters += lap.distance_meters;

      this.lap_data.push(lap);

    });

  }

  updateChart = () => {

    this.updateLapData();

    // Set the domains for the scales
    if (this.props.x_scale_mode == 'distance') {
      this.x_scale.domain([0, this.cumulative_meters]);
    }
    else {
      this.x_scale.domain([0, this.cumulative_seconds]);
    }

    if (this.props.x_scale_mode == 'distance') {
      this.x_axis.tickFormat(this.formatDistance);
    }
    else {
      this.x_axis.tickFormat(xhale.helper.format_time_seconds);
    }

    this.x_axis_group.call(this.x_axis);

    this.updateLapBars();

    this.updateLengthBars();

    // Add white lines between the bars so that if they are grouped together
    // without a rest showing, the bars look separate.
    let bar_separators = this.bar_separator_group.selectAll('.bar-separator').data(this.lap_data, (d, index) => {
      return d.id;
    });

    bar_separators.exit().remove();

    let new_bar_separators = bar_separators.enter()
      .append("line")
        .attr("class", "bar-separator")

    let all_bar_separators = new_bar_separators.merge(bar_separators)
      .attr("stroke", 'white')
      .attr("x1", d => {
        if (this.props.x_scale_mode == 'distance') {
          return this.x_scale(d.cumulative_meters + d.distance_meters);
        }
        else {
          return this.x_scale(d.cumulative_seconds + d.duration_seconds);
        }
      })
      .attr("x2", (d) => {
        if (this.props.x_scale_mode == 'distance') {
          return this.x_scale(d.cumulative_meters + d.distance_meters);
        }
        else {
          return this.x_scale(d.cumulative_seconds + d.duration_seconds);
        }
      })
      .attr("y1", 0)
      .attr("y2", this.chart_height);

    this.updatePaceLines();

  }

  updateLapBars = () => {

    // Add the lap bars.
    const bar_colour = get_discipline_colour('swimming');

    let lap_bars = this.lap_bar_group.selectAll('.bar').data(this.lap_data, (d, index) => {
      return d.id;
    });

    lap_bars.exit().remove();

    let new_lap_bars = lap_bars.enter()
      .append("rect")
        .attr("class", "bar lap-bar")

    let all_lap_bars = new_lap_bars.merge(lap_bars)
      .style('cursor', 'pointer')
      .attr("fill", d => {
        if (d.swim_stroke == 'resting') {
          return '#92e2ff';
        }
        else if (d.lengths_visible) {
          return '#0087d542' ;
        }
        else if (this.props.selected_lap_ids.indexOf(d.id) >= 0 || this.props.highlighted_lap_data?.lap_id == d.id) {
          return '#92e2ff';
        }
        else {
          return bar_colour
        }

      })
      .attr("x", d => {
        if (this.props.x_scale_mode == 'distance') {
          return this.x_scale(d.cumulative_meters);
        }
        else {
          return this.x_scale(d.cumulative_seconds);
        }
      })
      .attr("width", d => {
        if (this.props.x_scale_mode == 'distance') {
          return this.x_scale(d.distance_meters);
        }
        else {
          return this.x_scale(d.duration_seconds);
        }
      })
      .attr("y", (d) => {
        if (d.swim_stroke == 'resting') {
         return this.chart_height - 5;
        }
        else {
         return this.y_scale(d.seconds_per_hectometer)
        }
      })
      .attr("height", (d) => {
        if (d.swim_stroke == 'resting') {
         return 5;
        }
        else {
         return this.chart_height - this.y_scale(d.seconds_per_hectometer);
        }
      })
      .on('click', this.handleBarClick)
      .on('mouseover', this.handleBarMouseOver)
      .on('mouseout', this.handleBarMouseOut);
  }

  updateLengthBars = () => {

    // Add the length bars.
    const bar_colour = get_discipline_colour('swimming');

    let length_bars = this.length_bar_group.selectAll('.bar').data(this.length_data, (d, index) => {
      return d.id;
    });

    length_bars.exit().remove();

    let new_length_bars = length_bars.enter()
      .append("rect")
        .attr("class", "bar length-bar")

    let all_length_bars = new_length_bars.merge(length_bars)
      .style('cursor', 'pointer')
      .attr("fill", d => {
        return bar_colour
      })
      .attr("x", d => {
        if (this.props.x_scale_mode == 'distance') {
          return this.x_scale(d.cumulative_meters);
        }
        else {
          return this.x_scale(d.cumulative_seconds);
        }
      })
      .attr("width", d => {
        if (this.props.x_scale_mode == 'distance') {
          return this.x_scale(d.distance_meters);
        }
        else {
          return this.x_scale(d.duration_seconds);
        }
      })
      .attr("y", (d) => {
        if (d.swim_stroke == 'resting') {
         return this.chart_height - 5;
        }
        else {
         return this.y_scale(d.seconds_per_hectometer)
        }
      })
      .attr("height", (d) => {
        if (d.swim_stroke == 'resting') {
         return 5;
        }
        else {
         return this.chart_height - this.y_scale(d.seconds_per_hectometer);
        }
      })
      .on('click', this.handleBarClick)
      .on('mouseover', this.handleBarMouseOver)
      .on('mouseout', this.handleBarMouseOut);
  }

  updatePaceLines = () => {

    let pointer_starting_x_point = 5;
    Object.keys(this.props.target_swim_paces).forEach((index) => {

      let pace_data = target_swim_paces[index];

      if (pace_data.visible === true) {

        $('.pace-line-' + pace_data.id).show();

        // Calculate the width of this label's text element.
        var text_element = select(".pace-label.pace-line-" + pace_data.id);
        var label_text_width = text_element.node().getComputedTextLength();

        // Position each element along the x axis.
        selectAll(".pace-label.pace-line-" + pace_data.id).attr("dx", pointer_starting_x_point + 5);
        select(".pace-label-background.pace-line-" + pace_data.id).attr("width", label_text_width + 6).attr("x", pointer_starting_x_point + 2);
        select(".pace-label-pointer-background.pace-line-" + pace_data.id).attr("x1", pointer_starting_x_point).attr("x2", pointer_starting_x_point);
        select(".pace-label-pointer.pace-line-" + pace_data.id).attr("x1", pointer_starting_x_point).attr("x2", pointer_starting_x_point);

        // Position each element along the y axis.
        let pace = metersPerSecondAsSecondsPerHundredMeters(pace_data.meters_per_second);

        selectAll(".data-line.pace-line-" + pace_data.id)
          .attr("y1", this.y_scale(pace))
          .attr("y2", this.y_scale(pace));

        selectAll(".pace-label-pointer-background.pace-line-" + pace_data.id)
          .attr("y1", 10)
          .attr("y2", this.y_scale(pace) - 1);

        selectAll(".pace-label-pointer.pace-line-" + pace_data.id)
          .attr("y1", 10)
          .attr("y2", this.y_scale(pace));

        // Record how much further along we need to put the next label.
        pointer_starting_x_point += label_text_width + 20;

      }
      else {
        $('.pace-line-' + pace_data.id).hide();
      }
    });

  }

  addPaceLineToChart = (chart_container, chart, y_axis_label, chart_height) => {
    let self = this;

    const pointer_starting_x_point = 5;

    // Cycle through each pace.
    for (let i = 0; i < this.props.target_swim_paces.length; i++) {

      const pace = metersPerSecondAsSecondsPerHundredMeters(this.props.target_swim_paces[i].meters_per_second);

      // The actual pace line.
      chart.append("line")
        .attr("x1", 0)
        .attr("x2", this.props.chart_width)
        .attr("shape-rendering", 'crispEdges')
        .attr("stroke-dasharray", '2')
        .attr("stroke-width", '2')
        .style("stroke", '#ff8c00')
        .style('display', 'none')
        .attr("class", 'data-line pace-line pace-line-' + this.props.target_swim_paces[i].id);

      // Label.
      // ...a line pointing to the label.
      chart.append("line")
        .attr("x1", pointer_starting_x_point)
        .attr("x2", pointer_starting_x_point)
        .attr("fill", 'none')
        .attr("stroke-width", '2')
        .attr("stroke-dasharray", '2')
        .attr("shape-rendering", 'crispEdges')
        .attr("class", 'data-line pace-label-pointer pace-line-' + this.props.target_swim_paces[i].id)
        .attr("pace", this.props.target_swim_paces[i].meters_per_second)
        .style("stroke", '#ff8c00')
        .style('display', 'none');

      const text = this.props.target_swim_paces[i].description + ' - ' + formatSecondsAsPace(pace) + ' min/100m';

      // Speed label and label background.
      chart.append("rect")
        .attr("x", pointer_starting_x_point + 2)
        .attr("y", 0)
        .attr("height", 22)
        .attr("fill", 'white')
        .attr("class", 'pace-label-background pace-line-' + this.props.target_swim_paces[i].id)
        .style('display', 'none')
        .style("opacity", "0.8");

      chart.append("text")
        .attr("class", 'pace-label pace-label-100m pace-line-' + this.props.target_swim_paces[i].id)
        .style("font-size", "11px")
        .style("font-weight", "normal")
        .style('display', 'none')
        .style('text-anchor', 'start')
        .text(text)
        .attr("dx", pointer_starting_x_point + 5)
        .attr("dy", 15);

    }

    // Show an extra button on swimming data so that we can see a popup
    // with extra options for this chart.
    const extra_button = this.chart_container.append("g")
      .attr("transform", "translate(" + chart_margin.left + "," + (chart_margin.top + 40) + ")");

    extra_button.append("rect")
      .attr("x", -51)
      .attr("y", -4)
      .attr("width", 16)
      .attr("height", 16)
      .attr("fill", '#eee')

    extra_button.append("text")
      .style("font-size", "15px")
      .style("text-anchor", "middle")
      .text("...")
      .attr("dx", -43)
      .attr("dy", 5);

    extra_button.append("rect")
      .attr("class", "show-extra-button show-extra-paces-popup")
      .attr("x", -51)
      .attr("y", -4)
      .attr("width", 16)
      .attr("height", 16)
      .attr("fill", 'transparent')
      .on("click", function () {
        const offset = $(this)[0].getBoundingClientRect().top;
        self.props.openSwimPacePopup();
        if (!self.props.mobile_mode) {
          const height_of_popup = $('.chart-speed-configuration-popup-inner').height();
          $('.chart-speed-configuration-popup-inner').css('top', offset - height_of_popup - 17);
        }
      });

      y_axis_label.attr("dx", -15 - (chart_height / 2));
  }

  handleBarClick = (pointer_event, d) => {
    this.props.selectSwimLap(d.id, 'toggle');
  }

  handleBarMouseOver = (pointer_event, d) => {
    this.props.setSwimLapMouseHover(d.id);
  }

  handleBarMouseOut = (pointer_event, d) => {
    this.props.setSwimLapMouseHover(null);
  }

  componentDidMount = () => {
    this.createChart();
    this.updateChart();
  }

  componentDidUpdate = () => {
    this.updateChart();
  }

  render = () => {
    return (
      <div className={'swim-speed-chart-container chart-container'}>
        <div
          className={'swim-speed-chart'}
          style={{height: this.chart_height_with_margin + 'px', width: '100%', clear: 'both'}}>
            <svg style={{height: '100%'}} className="chart"></svg>
          </div>
      </div>
    );
  }

}

export class ChartSectionSwimLapsStrokes extends React.Component {

  chart_height_with_margin = 200
  chart_height = this.chart_height_with_margin - chart_margin.top - chart_margin.bottom;

  createChart = () => {

    this.updateLapData();

    let container_id = '#activity-' + this.props.activity_id + ' .swim-strokes-chart-container';
    let width = $(container_id).width() - chart_margin.left - chart_margin.right;

    this.chart_container = select(container_id + ' .chart')
      .attr("width", width + chart_margin.left + chart_margin.right);

    this.chart = this.chart_container.append("g")
      .attr("transform", "translate(" + chart_margin.left + "," + chart_margin.top + ")");

    // Set the scales
    this.x_scale = scaleLinear()
      .range([0, width])

    this.y_scale = scaleLinear()
      .range([this.chart_height, 0]);

    // Y axis label.
    this.y_axis_label = this.chart.append("text")
      .attr("class", "y-axis-label")
      .style("font-size", "12px")
      .style("text-anchor", "middle")
      .text('Strokes per minute')
      .attr("transform", "rotate(270)")
      .attr("dx", 0 - (this.chart_height / 2))
      .attr("dy", 15 - chart_margin.left);

    this.y_scale.domain([
      0,
      max(this.lap_data, d => d.strokes_per_minute)
    ]);

    this.x_axis = axisBottom()
      .scale(this.x_scale)

    let y_axis = axisLeft()
      .scale(this.y_scale)
      .ticks(4);

    // Add the x-axis
    this.x_axis_group = this.chart.append("g")
      .attr("transform", `translate(0, ${this.chart_height})`)
      .attr("class", "x axis lap-duration")
      .call(this.x_axis);

    // Add the y-axis
    this.chart.append("g")
      .attr("class", "y axis swim-duration")
      .call(y_axis);

    this.length_bar_group = this.chart.append("g")
      .attr("class", "length-bars")

    this.lap_bar_group = this.chart.append("g")
      .attr("class", "lap-bars")

    this.bar_separator_group = this.chart.append("g")
      .attr("class", "bar-separators")

  }

  updateLapData = () => {
    this.lap_data = [];
    this.length_data = [];

    this.cumulative_seconds = 0;
    this.cumulative_meters = 0;
    Object.keys(this.props.laps).forEach((lap_id) => {

      const original_lap = this.props.laps[lap_id];

      if (original_lap.creator != 'device' || original_lap.visible == false) {
        return;
      }

      let seconds_per_hectometer = null;
      let duration_seconds = 0;
      let distance_meters = 0;
      if (original_lap.swim_stroke != 'resting') {
        seconds_per_hectometer = metersPerSecondAsSecondsPerHundredMeters(original_lap.get_speed_meters_per_second());
        distance_meters = original_lap.distance_meters;
      }

      let lap = {
        id: original_lap.id,
        swim_stroke: original_lap.swim_stroke,
        distance_meters: distance_meters,
        duration_seconds: original_lap.duration_seconds,
        cumulative_seconds: this.cumulative_seconds,
        cumulative_meters: this.cumulative_meters,
        seconds_per_hectometer: seconds_per_hectometer,
        lengths_visible: original_lap.lengths_visible,
        strokes_per_minute: original_lap.average_cadence_rpm,
      };

      if (original_lap.lengths_visible) {

        let cumulative_lengths_meters = this.cumulative_meters;
        let cumulative_lengths_seconds = this.cumulative_seconds;

        Object.keys(original_lap.lengths).forEach((index) => {

          const length = original_lap.lengths[index];

          let length_data = {
            id: length.id,
            swim_stroke: length.stroke,
            stroke_count: length.strokes,
            distance_meters: length.distance_meters,
            duration_seconds: length.duration_seconds,
            cumulative_meters: cumulative_lengths_meters,
            cumulative_seconds: cumulative_lengths_seconds,
            seconds_per_hectometer: metersPerSecondAsSecondsPerHundredMeters(length.get_speed_meters_per_second()),
          };

          cumulative_lengths_meters += length.distance_meters;
          cumulative_lengths_seconds += length.duration_seconds;

          this.length_data.push(length_data);

        });

      }

      this.cumulative_seconds += lap.duration_seconds;
      this.cumulative_meters += lap.distance_meters;

      this.lap_data.push(lap);

    });

  }

  updateChart = () => {

    this.updateLapData();

    // Set the domains for the scales
    if (this.props.x_scale_mode == 'distance') {
      this.x_scale.domain([0, this.cumulative_meters]);
    }
    else {
      this.x_scale.domain([0, this.cumulative_seconds]);
    }

    if (this.props.x_scale_mode == 'distance') {
      this.x_axis.tickFormat(this.formatDistance);
    }
    else {
      this.x_axis.tickFormat(xhale.helper.format_time_seconds);
    }

    this.x_axis_group.call(this.x_axis);

    this.updateLapBars();

    this.updateLengthBars();

    // Add white lines between the bars so that if they are grouped together
    // without a rest showing, the bars look separate.
    let bar_separators = this.bar_separator_group.selectAll('.bar-separator').data(this.lap_data, (d, index) => {
      return d.id;
    });

    bar_separators.exit().remove();

    let new_bar_separators = bar_separators.enter()
      .append("line")
        .attr("class", "bar-separator")

    let all_bar_separators = new_bar_separators.merge(bar_separators)
      .attr("stroke", 'white')
      .attr("x1", d => {
        if (this.props.x_scale_mode == 'distance') {
          return this.x_scale(d.cumulative_meters + d.distance_meters);
        }
        else {
          return this.x_scale(d.cumulative_seconds + d.duration_seconds);
        }
      })
      .attr("x2", (d) => {
        if (this.props.x_scale_mode == 'distance') {
          return this.x_scale(d.cumulative_meters + d.distance_meters);
        }
        else {
          return this.x_scale(d.cumulative_seconds + d.duration_seconds);
        }
      })
      .attr("y1", 0)
      .attr("y2", this.chart_height);

  }

  updateLapBars = () => {

    // Add the lap bars.
    const bar_colour = get_discipline_colour('swimming');

    let lap_bars = this.lap_bar_group.selectAll('.bar').data(this.lap_data, (d, index) => {
      return d.id;
    });

    lap_bars.exit().remove();

    let new_lap_bars = lap_bars.enter()
      .append("rect")
        .attr("class", "bar lap-bar")

    let all_lap_bars = new_lap_bars.merge(lap_bars)
      .style('cursor', 'pointer')
      .attr("fill", d => {
        if (d.swim_stroke == 'resting') {
          return '#92e2ff';
        }
        else if (d.lengths_visible) {
          return '#0087d542' ;
        }
        else if (this.props.selected_lap_ids.indexOf(d.id) >= 0 || this.props.highlighted_lap_data?.lap_id == d.id) {
          return '#92e2ff';
        }
        else {
          return bar_colour
        }

      })
      .attr("x", d => {
        if (this.props.x_scale_mode == 'distance') {
          return this.x_scale(d.cumulative_meters);
        }
        else {
          return this.x_scale(d.cumulative_seconds);
        }
      })
      .attr("width", d => {
        if (this.props.x_scale_mode == 'distance') {
          return this.x_scale(d.distance_meters);
        }
        else {
          return this.x_scale(d.duration_seconds);
        }
      })
      .attr("y", (d) => {
        if (d.swim_stroke == 'resting') {
         return this.chart_height - 5;
        }
        else {
         return this.y_scale(d.strokes_per_minute)
        }
      })
      .attr("height", (d) => {
        if (d.swim_stroke == 'resting') {
         return 5;
        }
        else {
         return this.chart_height - this.y_scale(d.strokes_per_minute);
        }
      })
      .on('click', this.handleBarClick)
      .on('mouseover', this.handleBarMouseOver)
      .on('mouseout', this.handleBarMouseOut);
  }

  updateLengthBars = () => {

    // Add the length bars.
    const bar_colour = get_discipline_colour('swimming');

    let length_bars = this.length_bar_group.selectAll('.bar').data(this.length_data, (d, index) => {
      return d.id;
    });

    length_bars.exit().remove();

    let new_length_bars = length_bars.enter()
      .append("rect")
        .attr("class", "bar length-bar")

    let all_length_bars = new_length_bars.merge(length_bars)
      .style('cursor', 'pointer')
      .attr("fill", d => {
        return bar_colour
      })
      .attr("x", d => {
        if (this.props.x_scale_mode == 'distance') {
          return this.x_scale(d.cumulative_meters);
        }
        else {
          return this.x_scale(d.cumulative_seconds);
        }
      })
      .attr("width", d => {
        if (this.props.x_scale_mode == 'distance') {
          return this.x_scale(d.distance_meters);
        }
        else {
          return this.x_scale(d.duration_seconds);
        }
      })
      .attr("y", (d) => {
        if (d.swim_stroke == 'resting') {
         return this.chart_height - 5;
        }
        else {
         return this.y_scale(d.strokes_per_minute)
        }
      })
      .attr("height", (d) => {
        if (d.swim_stroke == 'resting') {
         return 5;
        }
        else {
         return this.chart_height - this.y_scale(d.strokes_per_minute);
        }
      })
      .on('click', this.handleBarClick)
      .on('mouseover', this.handleBarMouseOver)
      .on('mouseout', this.handleBarMouseOut);
  }

  handleBarClick = (pointer_event, d) => {
    this.props.selectSwimLap(d.id, 'toggle');
  }

  handleBarMouseOver = (pointer_event, d) => {
    this.props.setSwimLapMouseHover(d.id);
  }

  handleBarMouseOut = (pointer_event, d) => {
    this.props.setSwimLapMouseHover(null);
  }

  componentDidMount = () => {
    this.createChart();
    this.updateChart();
  }

  componentDidUpdate = () => {
    this.updateChart();
  }

  render = () => {
    return (
      <div className={'swim-strokes-chart-container chart-container'}>
        <div
          className={'swim-strokes-chart'}
          style={{height: this.chart_height_with_margin + 'px', width: '100%', clear: 'both'}}>
            <svg style={{height: '100%'}} className="chart"></svg>
          </div>
      </div>
    );
  }

}



export class SwimLapRow extends React.Component {

  toggleRowVisibility = () => {

    // Reverse visiblity
    this.props.lap.visible = (this.props.lap.visible) ? false : true;

    if (this.props.lap.visible == false) {
      // If the lap row has been made invisible, also hide any lengths below it.
      this.props.lap.lengths_visible = false;
    }

    this.props.updateLap(this.props.lap);
  }

  displayLengths = (visible) => {
    this.props.lap.lengths_visible = visible;

    if (visible) {
      // If lengths are visible, the lap row (which is really a header) also
      // needs to be visible. However, if the lengths are set to invisible
      // the lap can still be shown.
      this.props.lap.visible = true;
    }

    this.props.updateLap(this.props.lap);
  }

  handleHideClicked = (event) => {
    this.toggleRowVisibility();
    event.stopPropagation();
  }

  /**
   * If the row is hidden, make it visible. If it is already visible
   * toggle displaying the lengths in the lap.
   */
  handleRowClicked = (event) => {

    if (!this.props.lap.visible) {
      this.toggleRowVisibility();
    }
    else {

      // Reverse visiblity
      const display_lengths = (this.props.lap.lengths_visible) ? false : true;
      this.displayLengths(display_lengths);

    }

  }

  handleMouseOver = () => {
    this.props.setSwimLapMouseHover(this.props.lap.id);
  }

  handleMouseOut = () => {
    this.props.setSwimLapMouseHover(null);
  }

  render() {

    let columns = [];

    if (this.props.lap_features.swim_stroke) {
      columns.push(<td key='swim-stroke' className="swim-stroke"><div className="cell-content">{this.props.lap.swim_stroke}</div></td>);
    }

    if (this.props.lap_features.distance) {
      if (this.props.lap.swim_stroke == 'resting') {
        columns.push(<td key='distance-meters'></td>);
      }
      else {
        if (this.props.pool_unit == 'yards') {
          columns.push(<td key='distance-yards' className="distance"><div className="cell-content">{xhale.helper.decimal_places(this.props.lap.get_total_distance_yards())}<span className="unit">yds</span></div></td>);
        }
        else {
          columns.push(<td key='distance-meters' className="distance"><div className="cell-content">{this.props.lap.get_total_distance_km() * 1000}<span className="unit">m</span></div></td>);
        }
      }
    }

    if (this.props.lap_features.duration) {
      columns.push(<td key='duration' className="duration"><div className="cell-content">{xhale.helper.format_time_seconds(this.props.lap.duration_seconds)}</div></td>);
    }

    if (this.props.lap_features.speed) {
      if (this.props.lap.swim_stroke == 'resting') {
        columns.push(<td key='speed-min-hectometer' className="speed"></td>);
      }
      else {
        columns.push(<td key='speed-min-hectometer' className="speed"><div className="cell-content">{xhale.helper.format_minutes_seconds(xhale.helper.meters_per_second_as_seconds_per_hectometer(this.props.lap.get_speed_meters_per_second()))}</div></td>);
      }
    }

    if (this.props.lap_features.heart_rate) {
      columns.push(<td key='heart-rate' className="heart-rate"><div className="cell-content">{xhale.helper.decimal_places(this.props.lap.average_heart_rate_bpm, 0)}<span className="unit">bpm</span></div></td>);
    }

    if (this.props.lap_features.num_lengths) {
      if (this.props.lap.swim_stroke == 'resting') {
        columns.push(<td key='num-lengths'></td>);
      }
      else {
        columns.push(<td key='num-lengths' className="num-lengths"><div className="cell-content">{this.props.lap.num_lengths}</div></td>);
      }
    }

    if (this.props.lap_features.has_lengths) {
      if (this.props.lap.swim_stroke == 'resting') {
        columns.push(<td key='lengths-arrow'></td>);
        var lengths_class = 'no-lengths';
      }
      else {
        var cell_content = (this.props.lap.lengths.length > 0) ? (<div className="cell-content">{String.fromCharCode(8595)}</div>) : '';
        columns.push(<td key='lengths-arrow' className="lengths-arrow">{cell_content}</td>);
        var lengths_class = 'has-lengths';
      }
    }
    else {
      var lengths_class = 'no-lengths';
    }

    columns.push(<td onClick={this.handleHideClicked} key='hide' className="hide"><div className="cell-content">hide</div></td>);

    let visibility_class = (this.props.lap.visible) ? 'visible' : 'hidden';
    let selected_class = (typeof(this.props.selected_lap_ids) != 'undefined' && this.props.selected_lap_ids.indexOf(this.props.lap.id) >= 0)  ? 'selected' : 'unselected';
    let highlighted_class = (this.props.highlighted)  ? 'highlighted' : 'un-highlighted';

    return (
      <tr
        className={lengths_class + ' ' + visibility_class + ' ' + selected_class + ' ' + highlighted_class}
        onClick={this.handleRowClicked}
        onMouseOver={this.handleMouseOver}
        onMouseOut={this.handleMouseOut}
      >
        {columns}
      </tr>
    );

  }
}
